import React, { useMemo, useState } from "react";
import Modal from "../common/Modal";
import CampaignThumb from "../subComponents/CampaignThumb";
import {
  OnAirPill,
  SlotSchedule,
  lastPlayed,
} from "../subComponents/CampaignRunCard";
import {
  CAMPAIGN_STATUS_BADGE,
  displayCreativeName,
  formatPlayDuration,
} from "../subComponents/campaignFormatting";
import {
  COVERAGE_STATUSES,
  cityOf,
  filterByCampaign,
  filterByStatus,
  groupCoverage,
  listCampaigns,
  regionOf,
} from "../../utils/coverageMap";
import type { CampaignStatus, MapScreen } from "../../schemas/campaignSchema";

/** Where the popup opens, and with which filters: whatever the page was on. */
export interface CoverageDetailsScope {
  city: string | null;
  region: string | null;
  screenId: string | null;
  statuses: Set<CampaignStatus>;
  creativeId: string;
}

interface CoverageDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Every screen, unfiltered: the popup does its own filtering. */
  screens: MapScreen[];
  initial: CoverageDetailsScope;
}

const STATUS_LABEL = COVERAGE_STATUSES.reduce(
  (acc, s) => ({ ...acc, [s.status]: s.label }),
  {} as Record<CampaignStatus, string>,
);

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

const SELECT =
  "rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 disabled:bg-gray-50 disabled:text-gray-400";

/**
 * The coverage map's "view details": every campaign on a city, an area or one
 * screen, laid out with room to read. It opens on whatever the side panel was
 * showing, and its own filters move it anywhere else without closing it — the
 * side panel is for finding your way, this is for reading.
 *
 * One table, grouped by screen: a heading row per screen, then a row per
 * campaign running there.
 */
const CoverageDetailsModal: React.FC<CoverageDetailsModalProps> = ({
  isOpen,
  onClose,
  screens,
  initial,
}) => {
  // Seeded once from the page: the caller mounts the popup fresh on each open,
  // so changes made here never leak back into the map's own filters.
  const [city, setCity] = useState(initial.city ?? "");
  const [region, setRegion] = useState(initial.region ?? "");
  const [screenId, setScreenId] = useState(initial.screenId ?? "");
  const [statuses, setStatuses] = useState(() => new Set(initial.statuses));
  const [creativeId, setCreativeId] = useState(initial.creativeId);

  const campaignOptions = useMemo(() => listCampaigns(screens), [screens]);
  const matching = useMemo(
    () => filterByStatus(filterByCampaign(screens, creativeId), statuses),
    [screens, creativeId, statuses],
  );

  // The place dropdowns list only what the other filters leave, and a choice
  // they retire falls back to "all" — the same rule as the page's panel.
  const cities = useMemo(() => groupCoverage(matching), [matching]);
  const activeCity = cities.find((c) => c.city === city) ?? null;
  const regions = activeCity?.regions ?? [];
  const activeRegion = regions.find((r) => r.region === region) ?? null;
  const screenOptions = activeRegion?.screens ?? [];
  const activeScreen = screenOptions.find((s) => s.screenId === screenId) ?? null;

  const shown = useMemo(
    () =>
      activeScreen
        ? [activeScreen]
        : matching.filter(
            (s) =>
              (!activeCity || cityOf(s) === activeCity.city) &&
              (!activeRegion || regionOf(s) === activeRegion.region),
          ),
    [matching, activeCity, activeRegion, activeScreen],
  );
  const grouped = useMemo(
    () => groupCoverage(shown).flatMap((c) => c.regions.flatMap((r) => r.screens)),
    [shown],
  );

  const totals = useMemo(() => {
    const campaigns = new Set<string>();
    let plays = 0;
    let onAir = 0;
    shown.forEach((s) =>
      s.campaigns.forEach((c) => {
        campaigns.add(c.creativeId);
        plays += c.plays;
        if (c.isPlayingNow) onAir += 1;
      }),
    );
    return { campaigns: campaigns.size, plays, onAir };
  }, [shown]);

  const title = activeScreen
    ? activeScreen.screenName
    : activeRegion
      ? `${activeRegion.region}, ${activeCity?.city}`
      : activeCity?.city ?? "All cities";

  const toggleStatus = (status: CampaignStatus) =>
    setStatuses((current) => {
      const next = new Set(current);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="full">
      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label="City"
            className={SELECT}
            value={activeCity?.city ?? ""}
            onChange={(e) => {
              setCity(e.target.value);
              setRegion("");
              setScreenId("");
            }}
          >
            <option value="">All cities</option>
            {cities.map((c) => (
              <option key={c.city} value={c.city}>
                {c.city}
              </option>
            ))}
          </select>
          <select
            aria-label="Area"
            className={SELECT}
            value={activeRegion?.region ?? ""}
            disabled={!activeCity}
            onChange={(e) => {
              setRegion(e.target.value);
              setScreenId("");
            }}
          >
            <option value="">All areas</option>
            {regions.map((r) => (
              <option key={r.region} value={r.region}>
                {r.region}
              </option>
            ))}
          </select>
          <select
            aria-label="Screen"
            className={`${SELECT} max-w-[16rem]`}
            value={activeScreen?.screenId ?? ""}
            disabled={!activeRegion}
            onChange={(e) => setScreenId(e.target.value)}
          >
            <option value="">All screens</option>
            {screenOptions.map((s) => (
              <option key={s.screenId} value={s.screenId}>
                {s.screenName}
              </option>
            ))}
          </select>

          <span className="mx-1 hidden h-6 w-px bg-gray-200 sm:block" />

          {COVERAGE_STATUSES.map(({ status, label, color }) => {
            const on = statuses.has(status);
            return (
              <button
                key={status}
                type="button"
                aria-pressed={on}
                onClick={() => toggleStatus(status)}
                className={`flex items-center gap-2 rounded border px-3 py-2 text-sm font-medium ${
                  on
                    ? "border-primary-default bg-primary-default text-white"
                    : "border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
                }`}
              >
                <span
                  className="h-2.5 w-2.5 rounded-full border border-white"
                  style={{ backgroundColor: color }}
                />
                {label}
              </button>
            );
          })}

          <select
            aria-label="Campaign"
            className={`${SELECT} max-w-[16rem]`}
            value={creativeId}
            onChange={(e) => setCreativeId(e.target.value)}
          >
            <option value="">All campaigns</option>
            {campaignOptions.map((o) => (
              <option key={o.creativeId} value={o.creativeId}>
                {displayCreativeName(o.name)}
              </option>
            ))}
          </select>
        </div>

        <p className="text-sm text-gray-600">
          <b className="text-gray-900">{plural(shown.length, "screen")}</b> ·{" "}
          <b className="text-gray-900">{plural(totals.campaigns, "campaign")}</b> ·{" "}
          <b className="text-gray-900">{totals.plays.toLocaleString()}</b> plays
          {totals.onAir > 0 && (
            <span className="ml-2 align-middle">
              <OnAirPill count={totals.onAir} />
            </span>
          )}
        </p>

        {grouped.length === 0 ? (
          <p className="py-10 text-center text-sm text-gray-500">
            {statuses.size === 0
              ? "Turn on Live, Upcoming or Expired to see campaigns."
              : "No campaigns match these filters."}
          </p>
        ) : (
          <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-gray-200">
            <table className="w-full min-w-[56rem] text-left text-sm">
              <thead className="sticky top-0 z-10 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Campaign</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 text-right font-medium">Plays</th>
                  <th className="px-4 py-2 text-right font-medium">On screen</th>
                  <th className="px-4 py-2 font-medium">Last played</th>
                  <th className="px-4 py-2 font-medium">Schedule</th>
                </tr>
              </thead>
              {grouped.map((screen) => (
                <tbody key={screen.screenId} className="border-t border-gray-200">
                  <tr className="bg-gray-50/60">
                    <th colSpan={6} className="px-4 py-2 text-left font-normal">
                      <span className="font-semibold text-gray-900">
                        {screen.screenName}
                      </span>
                      <span className="ml-2 text-xs text-gray-500">
                        {[regionOf(screen), cityOf(screen)].join(", ")}
                        {screen.address ? ` · ${screen.address}` : ""}
                      </span>
                      <span
                        className={`ml-2 rounded-full px-2 py-0.5 text-xs font-medium ${
                          screen.screenOnline
                            ? "bg-success-100 text-success-700"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {screen.screenOnline ? "Online" : "Offline"}
                      </span>
                    </th>
                  </tr>
                  {screen.campaigns.map((campaign) => (
                    <tr
                      key={campaign.creativeId}
                      className="border-t border-gray-100 align-top"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <CampaignThumb
                            thumbnailUrl={campaign.thumbnailUrl}
                            className="h-10 w-16 flex-shrink-0"
                          />
                          <span
                            title={campaign.name}
                            className="font-medium text-gray-900"
                          >
                            {displayCreativeName(campaign.name)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col items-start gap-1">
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                              CAMPAIGN_STATUS_BADGE[campaign.status].className
                            }`}
                          >
                            {STATUS_LABEL[campaign.status]}
                          </span>
                          {campaign.isPlayingNow && <OnAirPill />}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-900">
                        {campaign.plays.toLocaleString()}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-gray-700">
                        {formatPlayDuration(campaign.playDuration)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-gray-700">
                        {lastPlayed(campaign.lastPlayedAt)}
                      </td>
                      <td className="px-4 py-3">
                        <SlotSchedule slots={campaign.slots} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CoverageDetailsModal;
